import React, { useMemo, useRef } from "react";
import PropTypes from "prop-types";
import Image from "../../general-components/image/image";
import classNames from "classnames";
import { motion, useScroll, useTransform } from "framer-motion";
import ColorMask from "@default/js/participants/components/color-mask";
import CustomHyphenation from "@default/general-components/custom-hyphenation";
import { generateSpeakerName } from "@default/lib/generate-speaker-name";
import { isPromoted } from "@default/lib/is-promoted";
import useAccessibilityContext from "@default/js/accessibility/store/use-accessibility-context";
import MetaInfos from "@default/js/participants/components/meta-infos";

const colorSchemes = [
  {
    name: "scheme-1",
    titleColor: "theme-4",
    blurColor: "theme-3",
  },
  {
    name: "scheme-2",
    titleColor: "theme-1",
    blurColor: "theme-5",
  },
  {
    name: "scheme-3",
    titleColor: "theme-3",
    blurColor: "theme-1",
  },
];

const DetailPageIntro = ({ content, mode }) => {
  const { simpleDesign } = useAccessibilityContext();

  /* Events of type "Showcase" and "Networking" are always sponsored. Therefore, the images should be uncut and shown differently.
   *  Exception: If User checks "fieldSponsoredCroppedImage", we'll show the image here normally.
   */

  const showImage = isPromoted(content)
    ? content.fieldSponsoredCroppedImage && !!content.fieldImage
    : !!content.fieldImage;

  const randomColorScheme = useMemo(() => {
    if (showImage) {
      const colorScheme =
        colorSchemes[Math.floor(Math.random() * colorSchemes.length)];

      if (typeof window !== "undefined") {
        document?.body.setAttribute("data-color-scheme", colorScheme?.name);
      }
      return colorScheme;
    } else {
      const colorScheme =
        colorSchemes[Math.floor(Math.random() * (2 - 1 + 1) + 1)];

      if (typeof window !== "undefined") {
        document?.body.setAttribute("data-color-scheme", colorScheme?.name);
      }
      return colorScheme;
    }
  }, []);

  const detailPageIntro = useRef(null);

  const { scrollY } = useScroll();

  // Diese Hooks müssen immer aufgerufen werden
  const rawOpacity = useTransform(scrollY, [100, 500], [1, 0]);
  const rawFilter = useTransform(
    scrollY,
    [100, 250],
    ["blur(0px)", "blur(20px)"]
  );

  // Konditional verwenden
  const opacity = simpleDesign ? undefined : rawOpacity;
  const filter = simpleDesign ? undefined : rawFilter;

  const introClassNames = classNames({
    "detail-page-intro": true,
    [mode]: true,
    "with-image": showImage,
    "without-image": !showImage,
  });

  let label = content.label;

  if (mode === "speaker") {
    label = generateSpeakerName(content);
  }

  let titleLength = content.label?.replace(/&shy;/g, "").length;

  const titleClassNames = classNames({
    title: true,
    "headline-xxl": titleLength <= 20,
    "headline-xl": titleLength > 20,
    [`color-${randomColorScheme?.titleColor}`]: showImage,
    container: content.entityBundle === "event",
    "main-container": content.entityBundle === "event" && !showImage,
  });

  const subtitleClassNames = classNames({
    "headline-s title": true,
    [`color-${randomColorScheme?.titleColor}`]: showImage,
  });

  const imageWrapperClassNames = classNames({
    "col-12 col-lg-8 offset-lg-2 col-xl-6 offset-xl-3":
      (mode === "act" || mode === "event") && !simpleDesign,
    "col-12 col-lg-6": mode === "speaker" && !simpleDesign,
    "col-12 col-md-9 offset-md-2 col-lg-5 offset-lg-4": simpleDesign,
  });

  return (
    <div
      className={introClassNames}
      data-color-scheme={randomColorScheme?.name}
      ref={detailPageIntro}
    >
      {showImage ? (
        <motion.div
          style={{
            ...(opacity ? { opacity } : {}),
            ...(filter ? { filter } : {}),
          }}
          className="scroll-away-wrapper"
        >
          <div className="container-fluid no-padding">
            <div className="row">
              <div className={imageWrapperClassNames}>
                <Image
                  data={content.fieldImage?.fieldMediaImage}
                  nodeTitle={content.label}
                  style={content.fieldImage?.fieldMediaImage?.styleSpeaker}
                />
              </div>
            </div>
          </div>
          {mode !== "speaker" && (
            <ColorMask
              type={"act-detail"}
              className={randomColorScheme?.blurColor}
            />
          )}
        </motion.div>
      ) : (
        <div
          className="intro-color-wrapper"
          data-color={randomColorScheme?.blurColor}
        />
      )}

      {label && (
        <div className="title-wrapper container">
          <div className="main-title row">
            <h1 className={titleClassNames}>
              <span>
                <CustomHyphenation length={9} canIgnoreLength={true}>
                  {label}
                </CustomHyphenation>
              </span>
            </h1>

            {mode === "speaker" && !!content.fieldCountry?.list?.length && (
              <>
                {content.fieldCountry.list.map((country) => (
                  <div className="tag body-s" key={country}>
                    {country.value}
                  </div>
                ))}
              </>
            )}
          </div>

          {content.fieldSubline && (
            <div className="subtitle container">
              <h2 className={subtitleClassNames}>{content.fieldSubline}</h2>
            </div>
          )}

          {simpleDesign && mode === "event" && <MetaInfos content={content} />}
        </div>
      )}
    </div>
  );
};

export const detailPageIntroPropTypes = PropTypes.shape({
  id: PropTypes.string,
  title: PropTypes.string,
  fieldImage: PropTypes.shape({
    fieldMediaImage: PropTypes.shape({
      alt: PropTypes.string,
      title: PropTypes.string,
      style: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
  }),
});

DetailPageIntro.propTypes = {
  content: detailPageIntroPropTypes,
};

export default DetailPageIntro;
